import { formatMoney } from 'Common/functions/Money';
import humanizeMinutes from 'Utils/humanizeMinutes';
import ServiceProviderAvatarWithName from 'Common/components/ServiceProviderAvatarWithName';
import { formatServiceTypeSingular } from '@bookpeazy/common-js';
import { GetBookingCartByUid } from 'Common/functions/getBookingCartByUid';
import { GetServiceOfferingWithCustomerInputs } from 'Common/functions/getServiceOfferingWithCustomerInputs';
import { CustomerInputType } from 'Generated/graphql';
import dayjs from 'dayjs';
import NoSSR from 'Common/hoc/NoSSR';

type customerInputSelections = NonNullable<NonNullable<GetBookingCartByUid>['bookingSelections']['customerInputSelections']> | undefined | null;

type customerInputs = Pick<GetServiceOfferingWithCustomerInputs['customerInputs'][number], 'uid' | 'type' | 'options' | 'unitPrice' | 'title'>[]

type serviceOffering = Pick<GetServiceOfferingWithCustomerInputs, 'basePrice' | 'baseTime' | 'title'> & { customerInputs: customerInputs; } | undefined | null

export type RequestAppointmentSummaryProps = {
  subTotalPrice: number | undefined | null;
  subTotalPriceAfterDiscounts: number | undefined | null;
  hidePrice?: boolean;
  hideSubtotalCopy?: boolean;
  renderSubtotalPrice?: (price: number) => React.ReactNode;
  serviceProviderAvatarImageUrl: string | undefined | null;
  serviceProviderDisplayName: string | undefined;
  serviceType: string | undefined;
  customerInputSelections: customerInputSelections;
  serviceOffering: serviceOffering;
  locationStreetAddress: string | undefined | null;
  startDateTime: number | undefined | null;
  tipPercent: number | undefined | null;
  promoCodeSection?: React.ReactNode;
  suffix?: React.ReactNode | null;
  totalDurationMins: number;
  taxAmount?: number | null;
  additionalSubtotalWrapClasses?: string;
  locationNode?: React.ReactNode;
}

const RequestAppointmentSummary: React.FC<RequestAppointmentSummaryProps> = ({
  serviceOffering,
  subTotalPrice,
  subTotalPriceAfterDiscounts,
  hidePrice = false,
  hideSubtotalCopy = false,
  renderSubtotalPrice,
  serviceProviderAvatarImageUrl,
  serviceProviderDisplayName,
  serviceType,
  customerInputSelections,
  locationStreetAddress,
  startDateTime,
  tipPercent,
  promoCodeSection,
  suffix,
  totalDurationMins,
  taxAmount,
  additionalSubtotalWrapClasses = '',
  locationNode,
}) => {
  const tipAmount = tipPercent ? (subTotalPrice || 0) * (tipPercent || 0) / 100 : 0;

  return (
    <div
      className="flex flex-col mb-4 gap-8"
    >
      <ServiceProviderAvatarWithName
        serviceProviderAvatarImageUrl={serviceProviderAvatarImageUrl}
        displayName={serviceProviderDisplayName}
      >
        <span className={serviceType ? '' : 'skeleton-sm'}>
          {serviceType && formatServiceTypeSingular(serviceType)}
        </span>
      </ServiceProviderAvatarWithName>
      <div
        className="w-full flex flex-col gap-6"
      >
        <div
          className="flex flex-col gap-8 bg-paleOrange p-4"
        >
          <div
            className="w-full rounded flex flex-col gap-4"
          >
            <span className="font-bold uppercase">
              Your Appointment
            </span>

            <div className="flex flex-col">
              {typeof locationNode !== 'undefined' ? locationNode : locationStreetAddress === null || locationStreetAddress === undefined || (
                <span>
                  {locationStreetAddress}
                </span>
              )}
              {startDateTime === null || startDateTime === undefined || (
                <NoSSR>
                  <span>
                    {dayjs.unix(startDateTime).format('MMMM DD, YYYY')}
                  </span>
                  <span>
                    {dayjs.unix(startDateTime).format('h:mm A')}
                  </span>
                </NoSSR>
              )}
            </div>

            <div className="flex justify-between">
              <div>
                <span className={serviceOffering ? '' : 'skeleton-sm'}>
                  {serviceOffering?.title}
                </span>
                <span>
                  {totalDurationMins > 0 && humanizeMinutes(totalDurationMins, true)}
                </span>
                {suffix && (
                  <span>
                    {suffix}
                  </span>
                )}
              </div>
              <span className={serviceOffering ? 'font-bold' : 'skeleton-sm'}>
                {hidePrice || serviceOffering && serviceOffering.basePrice > 0 && formatMoney(serviceOffering.basePrice, true)}
              </span>
            </div>
          </div>

          <div
            className="flex flex-col gap-2"
          >
            {serviceOffering && customerInputSelections?.map(sel => {
              if (sel.optionValue === '') {
                return null;
              }

              const c = serviceOffering.customerInputs.find(ci => ci.uid === sel.customerInputUid);
              if (!c) {
                return null;
              }

              const isText = c.type === CustomerInputType.Text;
              const isCheckbox = c.type === CustomerInputType.Checkbox;
              const isNumber = c.type === CustomerInputType.Number;

              const opts = isCheckbox ?
                c.options.filter(
                  o => sel.optionValue.split(',').includes(o.uid)
                ) :
                [c.options.find(o => o.uid === sel.optionValue)];

              const formattedSelections = (() => {
                if (isText) {
                  return [sel.optionValue];
                } else if (isNumber && c.unitPrice) {
                  if (sel.optionValue) {
                    const qty = parseInt(sel.optionValue);
                    if (!isNaN(qty) && qty !== 0) {
                      const price = c.unitPrice * qty;
                      const formattedPrice = hidePrice ? '' : formatMoney(price, true);

                      return [formattedPrice];
                    }
                  }
                } else if (opts) {
                  const prices = opts.map(
                    o => {
                      if (o) {
                        if (o.price > 0) {
                          return `${o.value}${hidePrice ? '' : ` - ${formatMoney(o.price, true)}`}`;
                        }

                        return o.value;
                      }
                    }
                  );

                  return prices;
                }

                return [];
              })();

              const suffix = isNumber ? ` x${sel.optionValue}` : '';

              return (
                <div
                  key={c.uid}
                  className="flex justify-between"
                >
                  <span>{c.title}{suffix}</span>
                  <div className="flex">
                    {formattedSelections.map((s, i) =>
                      <span
                        key={i}
                        className="block font-bold line-clamp-2 max-w-56 text-ellipsis"
                      >
                        {s}
                        {i < formattedSelections.length - 1 && (<>,&nbsp;</>)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {promoCodeSection}

        <div className="border-solid border-b-[1px] border-b-gray-500 my-4">
        </div>

        <div
          className="flex justify-end"
        >
          <div
            className={`flex flex-col basis-1/2 justify-between gap-2 ${additionalSubtotalWrapClasses}`}
          >
            {hidePrice || (
              <div className="flex justify-between gap-6">
                {hideSubtotalCopy || (subTotalPrice ? (
                  <span className="">
                    Subtotal
                  </span>
                ) : null)}
                <span className="font-bold">
                  {subTotalPrice ? (renderSubtotalPrice ?
                    renderSubtotalPrice(subTotalPrice) :
                    formatMoney(subTotalPrice)) : ''}
                </span>
              </div>
            )}

            {hidePrice || taxAmount !== null && taxAmount !== undefined && taxAmount > 0 && (
              <div className="flex justify-between gap-6">
                <span className="">
                  Tax
                </span>
                <span className="font-bold">
                  {formatMoney(taxAmount)}
                </span>
              </div>
            )}

            {hidePrice || (tipAmount > 0 ? (
              <div className="flex justify-between gap-6">
                <span className="">
                  Tip ({tipPercent}%)
                </span>
                <span className="font-bold">
                  {formatMoney(tipAmount, true)}
                </span>
              </div>
            ) : <div className="h-5" />)}

            {hidePrice || ((taxAmount !== null && taxAmount !== undefined && subTotalPrice) ? (
              <div className="flex justify-between gap-6">
                <span className="">
                  Total
                </span>
                <span className="font-bold">
                  {formatMoney((subTotalPriceAfterDiscounts ?? subTotalPrice) + tipAmount + taxAmount)}
                </span>
              </div>
            ) : null)}
          </div>
        </div>

      </div>
    </div>
  );
};

export default RequestAppointmentSummary;

